import type { HelmInspectQuery } from '@features/fields/api/useHelmInspect'
import { BoundaryHoleBehavior } from 'api-validations/locutus/guidance'
import { Controller, type UseFormReturn, useWatch } from 'react-hook-form'
import { useLocalization } from 'shared-ui/localization'
import { planZoneColor, useMapContext, useSelectedId } from 'shared-ui/map'

import type { FormValues } from './formtypes'
import { holeKey, withOverride } from './overrideKeys'
import {
  InspectStates,
  OverrideRow,
  type PillOption,
  PillToggle,
  useViewOnlyFeatures
} from './shared'

const usePassableOptions = (): readonly [PillOption<boolean>, PillOption<boolean>] => {
  const { t } = useLocalization()
  return [
    {
      value: true,
      label: t(($) => $.planning.passable),
      subtleColor: 'bg-green-50 text-green-600',
      strongColor: 'bg-green-100 text-green-800'
    },
    {
      value: false,
      label: t(($) => $.planning.impassable),
      subtleColor: 'bg-red-50 text-red-500',
      strongColor: 'bg-red-100 text-red-700'
    }
  ]
}

export const Holes = ({
  form,
  inspectQuery
}: {
  form: UseFormReturn<FormValues>
  inspectQuery: HelmInspectQuery
}) => {
  const { t } = useLocalization()
  const { map } = useMapContext()
  const holes = inspectQuery.data?.holes ?? []

  const selectedHoleId = useSelectedId()
  const passableOptions = usePassableOptions()

  const holeOverrides = useWatch({ control: form.control, name: 'holeOverrides' })
  const setPassable = (key: string, passable: boolean | null) =>
    form.setValue(
      'holeOverrides',
      withOverride(holeOverrides, key, passable === null ? undefined : { key, passable }),
      { shouldDirty: true }
    )

  useViewOnlyFeatures('hole', holes, (_hole, i) => ({ color: planZoneColor(i) }))

  return (
    <InspectStates
      query={inspectQuery}
      isEmpty={holes.length === 0}
      emptyText={t(($) => $.planning.noHolesDrawn)}
    >
      <div className="flex flex-col gap-3 py-4">
        <p className="text-sm text-gray-500">{t(($) => $.planning.holesDescription)}</p>
        <div className="flex items-center justify-between">
          <div className="pan-title5">{t(($) => $.planning.interiorBoundaries)}</div>
          <Controller
            control={form.control}
            name="boundaryHoleBehavior"
            render={({ field }) => (
              <PillToggle
                value={field.value === BoundaryHoleBehavior.Passable}
                options={passableOptions}
                onChange={(v) =>
                  field.onChange(
                    v ? BoundaryHoleBehavior.Passable : BoundaryHoleBehavior.Impassable
                  )
                }
              />
            )}
          />
        </div>
        <div className="grid grid-cols-2 gap-2">
          {holes.map((hole, holeIndex) => {
            const key = holeKey(hole)
            const override = holeOverrides.find((o) => o.key === key)
            if (override === undefined) return null
            const isSelected = hole.id === selectedHoleId
            return (
              <OverrideRow
                key={hole.id}
                color={planZoneColor(holeIndex)}
                label={t(($) => $.planning.holeNumber, { number: holeIndex + 1 })}
                isSelected={isSelected}
                onSelect={() => map.drawTool.setSelected(isSelected ? null : hole.id)}
                isOverridden
                onReset={() => setPassable(key, null)}
              >
                <PillToggle
                  value={override.passable}
                  options={passableOptions}
                  onChange={(v) => setPassable(key, v)}
                />
              </OverrideRow>
            )
          })}
        </div>
      </div>
    </InspectStates>
  )
}
